import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { ArrowLeftRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_LABELS = { chef_poste: 'Chef de Poste', agent: 'Agent' };

// Permet à un compte affecté à plusieurs sites de basculer de l'un à l'autre
// sans se déconnecter : un nouveau jeton est émis pour le site choisi, ce qui
// recalcule le rôle et les autorisations propres à ce site. La page est
// ensuite rechargée pour que toutes les données soient relues avec ce jeton.
const SiteSwitcher = ({ user, iconOnly = false, dark = true, onSwitched }) => {
  const [sites, setSites] = useState([]);
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    if (!user?.id || user.role === 'admin') return;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/auth/my-sites`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (res.ok && !cancelled) setSites(await res.json());
      } catch { /* silencieux */ }
    };
    load();
    return () => { cancelled = true; };
  }, [user?.id, user?.role]);

  if (sites.length < 2) return null;

  const current = sites.find(s => s.id === user?.active_site_id);

  const handleChange = async (siteId) => {
    if (!siteId || siteId === user?.active_site_id) return;
    setSwitching(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/select-site`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ site_id: siteId })
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      localStorage.setItem('token', data.access_token);
      if (onSwitched) onSwitched();
      window.location.assign('/');
    } catch {
      toast.error('Impossible de changer de site');
      setSwitching(false);
    }
  };

  if (iconOnly) {
    return (
      <div className="flex justify-center" title={current ? `Site : ${current.name}` : 'Changer de site'}>
        <ArrowLeftRight className={`h-4 w-4 ${dark ? 'text-indigo-200' : 'text-gray-500'}`} />
      </div>
    );
  }

  return (
    <div data-testid="site-switcher">
      <label className={`flex items-center gap-1.5 text-xs mb-1 ${dark ? 'text-indigo-200' : 'text-gray-500 dark:text-slate-400'}`}>
        <ArrowLeftRight className="h-3.5 w-3.5" />
        Site
      </label>
      <select
        value={user?.active_site_id || ''}
        onChange={(e) => handleChange(e.target.value)}
        disabled={switching}
        data-testid="site-switcher-select"
        className={`w-full rounded-md px-2 py-1.5 text-sm border focus:outline-none focus:ring-2 focus:ring-indigo-400 disabled:opacity-50 ${
          dark
            ? 'bg-white/10 text-white border-white/30'
            : 'bg-white text-gray-800 border-gray-300 dark:bg-slate-800 dark:text-slate-100 dark:border-slate-600'
        }`}
      >
        {!current && <option value="" className="text-black">Choisir un site</option>}
        {sites.map(s => (
          <option key={s.id} value={s.id} className="text-black">
            {s.name} ({ROLE_LABELS[s.role] || s.role})
          </option>
        ))}
      </select>
    </div>
  );
};

export default SiteSwitcher;
